import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Divider,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { EditSectionPopup } from "../GenericForm/EditSectionPopup";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import StarIcon from "@mui/icons-material/Star";

import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { encryptId } from "../../Utils/SecurityFunctions";
import useSendPutRequest from "../../Hooks/useSendPutRequest";
import { useParams } from "react-router-dom";
import PageTitle from "@Components/PageTitle/PageTitle";
import { useNavigate } from "react-router-dom";
import themeCreator from "@Themes/themeCreator";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import axiosInstance from "@API/axios";
import {
  GET_TRANSACTION_DETAILS,
  GET_GROUP_TRANSACTION_DETAILS,
  UPDATE_GROUP_TRANSACTION_ORDER,
  UPDATE_TRANSACTION_ORDER,
  STAR_ORDERS_URL,
  UNSTAR_ORDERS_URL,
} from "@API/APIRoutes";
import { decryptId } from "../../Utils/SecurityFunctions";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import EditIcon from "@mui/icons-material/Edit";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import usePostItem from "@Hooks/usePostItem";

import {
  DebitAccount,
  inputTypes,
} from "../../Utils/HelpingObjects/HelpingObjects";
import { set } from "date-fns";
const dateFields = new Set([
  "authorizationDate",
  "form4SendingDate",
  "form4ReceivalDate",
  "invoiceDate",
  "bolDate",
  "maturityDate",
  "paymentDate",
  "uploadOnPortalDate"
]);

const capitalizeFromSnakeCase = (text) =>
  text
    ? text
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "";

const formatDate = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  return isNaN(parsed.getTime())
    ? "N/A"
    : parsed.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const formatValue = (key, value) => {
  if (key === "submittedOnPortal") {
    return value ? "Yes" : "No";
  }

  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (dateFields.has(key)) {
    return formatDate(value);
  }

  if (key === "transactionStatus" || key === "maturityType") {
    return capitalizeFromSnakeCase(value);
  }

  if(key === "proceededManually"){
    return value ? "Yes" : "No";
  }
  
  if (
    key === "invoiceAmount" ||
    key === "debitValue" ||
    key === "exchangeRate"
  ) {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      return value;
    }
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: key === "exchangeRate" ? 6 : 2,
    }).format(numberValue);
  }

  if (typeof value === "object") {
    if (value?.supplierName) {
      return value.supplierName;
    }
    if (value?.bankName) {
      return value.bankName;
    }
    if (value?.name) {
      return value.name;
    }
    return JSON.stringify(value);
  }

  return value;
};

const columnDefinition = [
  [
    { key: "poNumber", label: "PO Number" },
    { key: "supplier", label: "Supplier" },
    { key: "bank", label: "Bank" },
    { key: "invoiceNumber", label: "Invoice Number" },
    { key: "invoiceAmount", label: "Invoice Amount" },
    { key: "invoiceCurrency", label: "Invoice Currency" },
    { key: "invoiceDate", label: "Invoice Date" },
    { key: "bolDate", label: "BOL Date" },
  ],
  [
    { key: "maturityPeriod", label: "Maturity Period" },
    { key: "maturityType", label: "Maturity Type" },
    { key: "maturityDate", label: "Maturity Date" },
    { key: "form4SendingDate", label: "Form 4 Sending Date" },
    { key: "uploadOnPortalDate", label: "Upload on Portal Date" },
    { key: "authorizationDate", label: "Authorization Date" },
    { key: "form4ReceivalDate", label: "IDC Date" },
    { key: "idcNumber", label: "IDC Number" },
  ],
  [
    { key: "proceededManually", label: "Proceeded Manually ?" },
    { key: "submittedOnPortal", label: "Submitted on Portal" },
    { key: "coraId", label: "CORA ID" },
    { key: "transactionStatus", label: "Transaction Status" },
    { key: "paymentDate", label: "Payment Date" },
    { key: "debitAccount", label: "Debit Account" },
    { key: "exchangeRate", label: "Exchange Rate" },
    { key: "debitValue", label: "Debit Value" },
    { key: "transactionComments", label: "Transaction Comments" },
  ],
];

// Group view: each field shows the distinct values of the group's orders, comma-separated.
const formatGroupValue = (key, orders) => {
  const values = [
    ...new Set(
      orders
        .map((order) => formatValue(key, order[key]))
        .filter((value) => value !== "N/A")
    ),
  ];
  return values.length ? values.join(", ") : "N/A";
};

// Seeds the group edit form: the shared value when all orders agree, otherwise empty.
const getCommonValue = (orders, key, emptyValue) => {
  if (!orders.length) return emptyValue;
  const first = orders[0][key] ?? emptyValue;
  return orders.every((order) => (order[key] ?? emptyValue) === first)
    ? first
    : emptyValue;
};

function TreasuryCoordinatorTransactionDetails({ isEdit = true }) {
  const params = useParams();
  const groupId = params.groupId ? decryptId(params.groupId) : null;
  const isGroup = Boolean(groupId);
  const orderId = params.orderId ? decryptId(params.orderId) : null;
  const [groupOrders, setGroupOrders] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const theme = themeCreator();

  useEffect(() => {
    if (!orderId && !groupId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    axiosInstance
      .get(
        isGroup
          ? `${GET_GROUP_TRANSACTION_DETAILS}/${groupId}`
          : `${GET_TRANSACTION_DETAILS}/${orderId}`
      )
      .then((response) => {
        if (isGroup) {
          setGroupOrders(response.data);
        }
        setTransactionDetails(isGroup ? {} : response.data);
      })
      .catch((error) => {
        const message =
          error.response?.data?.details ||
          error.response?.data ||
          "Failed to fetch transaction details.";

        dispatch(
          setSnackbar({
            open: true,
            message,
            error: true,
          }),
        );
      })
      .finally(() => setIsLoading(false));
  }, [orderId, groupId, isGroup, reloadKey, dispatch]);

  const columns = useMemo(() => columnDefinition, []);
  //********************* Star Popup *********************//
  const { loading: starOrderLoading, postItem: starOrder } = usePostItem();
  const { loading: unstarOrderLoading, postItem: unstarOrder } = usePostItem();
  const [showStarPopup, setShowStarPopup] = useState(false);
  const [isUnstarAction, setIsUnstarAction] = useState(false);

  const openStarPopup = (OrderId) => {
    setIsUnstarAction(Boolean(transactionDetails?.isStarred));
    setShowStarPopup(true);
  };
  const closeStarPopup = () => setShowStarPopup(false);

  const ConfirmStarPopUp = (OrderId) => {
    if (isUnstarAction) {
      const confirmUnstarCallBack = () => {
        closeStarPopup();
        setTransactionDetails((prevDetails) => ({
          ...prevDetails,
          isStarred: false,
        }));
      };
      unstarOrder(
        UNSTAR_ORDERS_URL,
        { orderId: OrderId },
        confirmUnstarCallBack,
        `Order Unstarred Successfully!`,
        `Failed to Unstar Order!`,
      );
    } else {
      const confirmStarCallBack = () => {
        closeStarPopup();
        setTransactionDetails((prevDetails) => ({
          ...prevDetails,
          isStarred: true,
        }));
      };
      starOrder(
        STAR_ORDERS_URL,
        { orderId: OrderId },
        confirmStarCallBack,
        `Order Starred Successfully!`,
        `Failed to Star Order!`,
      );
    }
  };
  //********************* Edit Popup *********************//
  const [showEditPopup, setShowEditPopup] = useState(false);
  const { isLoading: PutItemLoading, requestMethod: putItem } =
    useSendPutRequest(true);
  const closeEditPopup = () => setShowEditPopup(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [sectionData, setSectionData] = useState({});
  const confirmEdit = async (newData) => {
    if (isGroup) {
      const response = await putItem(
        UPDATE_GROUP_TRANSACTION_ORDER,
        { ...newData, groupId: Number(groupId) },
        `Group Transactions Edited Successfully!`,
      );
      if (response) {
        closeEditPopup();
        setReloadKey((key) => key + 1);
      }
      return;
    }
    let updatedOrderData = {
      ...newData,
      orderId: Number(orderId),
    };
    const response = await putItem(
      UPDATE_TRANSACTION_ORDER,
      updatedOrderData,
      `Order Edited Successfully!`,
    );
    // Update the rows state with the edited data
    setTransactionDetails((prevDetails) => ({
      ...prevDetails,
      ...updatedOrderData,
    }));
    closeEditPopup();
  };

  const FormFields = [
    {
      size: 4,
      type: inputTypes.FLOAT_NUMBER,
      key: "exchangeRate",
      label: "Exchange Rate",
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.TEXT,
      key: "transactionComments",
      label: "Transaction Comments",
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "debitAccount",
      label: "Debit Account",
      options: DebitAccount,
      editable: true,
    },
    {
      size: 6,
      type: inputTypes.DATE,
      key: "authorizationDate",
      label: "Authorization Date",
      editable: true,
    },
    {
      size: 6,
      type: inputTypes.DATE,
      key: "paymentDate",
      label: "Payment Date",
      editable: true,
    },
    {
      size: 6,
      type: inputTypes.BOOLEAN,
      key: "submittedOnPortal",
      label: "Submitted on Portal",
      editable: true,
    },
  ];

  const openEditPopup = (rowData) => {
    if (!rowData) {
      return;
    }
    // Extract only editable fields from the selected row to seed the popup form
    const editableSectionData = FormFields.filter(
      (field) => field.editable,
    ).reduce((acc, field) => {
      if (isGroup) {
        acc[field.key] = getCommonValue(
          groupOrders,
          field.key,
          field.type === inputTypes.BOOLEAN ? false : "",
        );
        return acc;
      }
      acc[field.key] =
        field.type === inputTypes.BOOLEAN
          ? rowData[field.key] ?? false
          : rowData[field.key] !== undefined && rowData[field.key] !== null
          ? rowData[field.key]
          : "";
      return acc;
    }, {});

    setSectionData(editableSectionData);
    setShowEditPopup(true);
  };
  const handleInfoAction = () => {
    navigate(DefaultRoutes.orderInfo.replace(":orderId", encryptId(orderId)));
  };

  const editSectionPopup = EditSectionPopup(
    "Transaction",
    showEditPopup,
    closeEditPopup,
    confirmEdit,
    PutItemLoading,
    FormFields,
    sectionData,
    false,
    isPageLoading,
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  if (!transactionDetails) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <Typography variant="h6" color="text.secondary">
          Transaction details are not available.
        </Typography>
      </Box>
    );
  }

  return (
    <Container sx={{ pb: 5 }}>
      <Box
        sx={{
          my: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <PageTitle
          title={isGroup ? "Group Transaction Details" : "Transaction Details"}
          PageIcon={<AccountBalanceIcon color="white" fontSize="large" />}
          iconColor={theme.palette.primary.main}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          {!isGroup && (
            <Tooltip title="Full Order Info">
              <IconButton color="info" onClick={handleInfoAction} size="large">
                <InfoRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
          {isEdit && (
            <Tooltip title="Edit">
              <IconButton
                color="warning"
                onClick={() => {
                  openEditPopup(transactionDetails);
                }}
                size="large"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isGroup && (
            <Tooltip title={transactionDetails.isStarred ? "Unstar" : "Star"}>
              <IconButton
                color="primary"
                onClick={() => {
                  openStarPopup(orderId);
                }}
                size="large"
              >
                {transactionDetails.isStarred ? (
                  <StarIcon sx={{ color: theme.palette.warning.main }} />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          p: 4,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={{ xs: 3, md: 4 }}
        >
          {columns.map((column, columnIndex) => (
            <React.Fragment key={columnIndex}>
              <Box flex={1}>
                {column.map(({ key, label }) => (
                  <Box
                    key={key}
                    sx={{
                      mb: 3,
                      "&:last-of-type": { mb: 0 },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", fontWeight: 600 }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {isGroup
                        ? formatGroupValue(key, groupOrders)
                        : formatValue(key, transactionDetails[key])}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {columnIndex < columns.length - 1 && (
                <React.Fragment>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      display: { xs: "none", md: "block" },
                    }}
                  />
                  <Divider
                    orientation="horizontal"
                    sx={{
                      display: { xs: "block", md: "none" },
                    }}
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          ))}
          {editSectionPopup}
          {DeletePopup(
            "Order",
            showStarPopup,
            closeStarPopup,
            ConfirmStarPopUp,
            orderId,
            isUnstarAction ? unstarOrderLoading : starOrderLoading,
            isUnstarAction ? "Unstar" : "Star",
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default TreasuryCoordinatorTransactionDetails;
